import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DateRangeFilterProps {
  value?: DateRange;
  onChange: (range: DateRange | undefined) => void;
  placeholder?: string;
  triggerClassName?: string;
  contentClassName?: string;
  badgeLabel?: string;
  resetSelectionOnOpen?: boolean;
  disabled?: { after?: Date; before?: Date };
  numberOfMonths?: number;
  align?: "start" | "center" | "end";
}

export const DateRangeFilter = ({
  value,
  onChange,
  placeholder = "Date range",
  triggerClassName,
  contentClassName,
  badgeLabel,
  resetSelectionOnOpen = false,
  disabled,
  numberOfMonths = 2,
  align = "start",
}: DateRangeFilterProps) => {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<DateRange | undefined>(value);

  React.useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) setDraft(resetSelectionOnOpen ? undefined : value);
  };

  const handleSelect = (range: DateRange | undefined) => {
    setDraft(range);
    if (range?.from && range?.to) {
      onChange(range);
    } else if (!range) {
      onChange(undefined);
    }
  };

  const handleClear = () => {
    setDraft(undefined);
    onChange(undefined);
  };

  const label = draft?.from
    ? draft.to
      ? `${format(draft.from, "LLL dd, y")} - ${format(draft.to, "LLL dd, y")}`
      : format(draft.from, "LLL dd, y")
    : placeholder;

  return (
    <div className="relative flex items-center">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "justify-start px-2.5 font-normal",
              draft?.from && "bg-muted pr-8",
              triggerClassName
            )}
          >
            <CalendarIcon className="shrink-0" />
            <span className="truncate">{label}</span>
            {badgeLabel && (
              <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {badgeLabel}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn("w-auto p-0", contentClassName)}
          align={align}
        >
          <Calendar
            mode="range"
            defaultMonth={draft?.from}
            selected={draft}
            onSelect={handleSelect}
            numberOfMonths={numberOfMonths}
            disabled={disabled}
            autoFocus
          />
        </PopoverContent>
      </Popover>
      {draft?.from && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-2 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
          aria-label="Clear date range"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};
